import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { ALL_MODALITIES } from '../../tasks/queries/paths';
import { findingKeys } from '../queries/keys';
import { findingPaths } from '../queries/paths';
import { byUrgency, isFinding } from '../utils/severity';

// TSS vite/03 — server state for the findings screen.
//
// ONE REQUEST, SPLIT HERE. The task list filters by `workflow_state` and by
// nothing else that matters to us: there is no `origin_task_id` filter, so
// `proposed` comes back holding two different things — findings, which came
// out of somebody working, and tasks a master wrote and has not promoted yet.
// The split is done in the browser over one page of results, and the count of
// what was left out is returned so the screen can say it out loud. A filter
// that silently drops rows is how a list starts lying about its own total.

const PAGE = 100;   // the backend's hard cap on `limit`

export function useFindings({ projectId } = {}) {
    const params = useMemo(() => {
        // Both modalities: a finding opened from a one-off task is a one-off task.
        const query = { limit: PAGE, offset: 0, workflow_state: 'proposed', modality: ALL_MODALITIES };
        if (projectId) query.project_id = projectId;
        return query;
    }, [projectId]);

    const proposed = useQuery({
        queryKey: findingKeys.proposed(params),
        queryFn: ({ signal }) => api
            .get(findingPaths.proposed(), { params, signal })
            .then(response => response.data),
    });

    const items = useMemo(() => proposed.data?.items ?? [], [proposed.data]);
    const findings = useMemo(() => items.filter(isFinding).sort(byUrgency), [items]);
    const notFindings = items.length - findings.length;

    // The task each one came out of. Distinct ids only: three findings from the
    // same task are one request, not three. They are separate queries and not
    // a batch because the API has no batch — and because each one lands in
    // ['tasks','detail',id], which is the key the task screens already use, so
    // a title read here is a title the edit form does not ask for again.
    const originIds = useMemo(
        () => [...new Set(findings.map(finding => finding.origin_task_id))],
        [findings],
    );

    const originQueries = useQueries({
        queries: originIds.map(id => ({
            queryKey: findingKeys.origin(id),
            queryFn: ({ signal }) => api
                .get(findingPaths.origin(id), { signal })
                .then(response => response.data),
            staleTime: 5 * 60_000,
        })),
    });

    const origins = useMemo(() => {
        const map = new Map();
        originIds.forEach((id, index) => {
            const task = originQueries[index]?.data;
            if (task) map.set(id, task);
        });
        return map;
    }, [originIds, originQueries]);

    return {
        findings,
        origins,
        notFindings,
        // What the page holds against what the backend counted. They differ the
        // day there are more than a hundred proposed tasks, and the screen has
        // to be able to say so rather than quietly show the first page.
        loaded: items.length,
        total: proposed.data?.pagination?.total ?? 0,
        isLoading: proposed.isLoading,
        error: proposed.error,
    };
}
